import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useAuth } from '../App';

const TYPES = [
  { key: 'evenement', label: '📅 Événement' },
  { key: 'tache', label: '✅ Tâche' },
  { key: 'pensee', label: '💭 Pensée' },
  { key: 'courses', label: '🛒 Courses' },
];

const CATEGORY_ICONS = {
  menage: '🧹', linge: '👕', cuisine: '🍳', animaux: '🐾',
  courses: '🛒', admin: '📄', enfants: '👶', autre: '✅',
};

export default function QuickAdd({ type: initType = 'evenement', onClose }) {
  const { user } = useAuth();
  const [type, setType] = useState(initType);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Événement
  const [evTitle, setEvTitle] = useState('');
  const [evDate, setEvDate] = useState((() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })());
  const [evTime, setEvTime] = useState('');
  const [evWho, setEvWho] = useState('');
  const [evCat, setEvCat] = useState('autre');

  // Tâche
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDueMode, setTaskDueMode] = useState('today');
  const [taskDue, setTaskDue] = useState((() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })());
  const [taskWho, setTaskWho] = useState('');
  const [taskPriority, setTaskPriority] = useState('medium');
  const [taskRecurrence, setTaskRecurrence] = useState('une_fois');
  const [taskIcon, setTaskIcon] = useState('✅');

  // Pensée
  const [thought, setThought] = useState('');
  const [thoughtPriority, setThoughtPriority] = useState('normal');

  // Courses
  const [shopName, setShopName] = useState('');
  const [shopQty, setShopQty] = useState('');
  const [shopCat, setShopCat] = useState('Autre');

  useEffect(() => {
    api.get('/family/members').then(r => setMembers(r.data)).catch(() => {});
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const today = (() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();

  const handleSave = async () => {
    setSaving(true);
    try {
      if (type === 'evenement') {
        if (!evTitle.trim()) return;
        const dt = evTime ? `${evDate} ${evTime}:00` : `${evDate} 00:00:00`;
        await api.post('/events', {
          title: evTitle.trim(), start_datetime: dt,
          user_id: evWho || null, category: evCat, all_day: !evTime
        });
      } else if (type === 'tache') {
        if (!taskTitle.trim()) return;
        const duePayload = {};
        if (taskDueMode === 'today') {
          duePayload.next_due = today;
        } else if (taskDueMode === 'week') {
          duePayload.due_scope = 'week';
        } else if (taskDueMode === 'month') {
          duePayload.due_scope = 'month';
        } else if (taskDueMode === 'next_month') {
          const d = new Date(); d.setMonth(d.getMonth() + 2, 0);
          duePayload.next_due = d.toISOString().split('T')[0];
        } else if (taskDueMode === 'date') {
          duePayload.next_due = taskDue || null;
        } else {
          duePayload.next_due = null;
        }
        await api.post('/tasks', {
          title: taskTitle.trim(), ...duePayload,
          assigned_to: taskWho || null, priority: taskPriority,
          recurrence: taskRecurrence, icon: taskIcon, category: 'autre'
        });
      } else if (type === 'pensee') {
        if (!thought.trim()) return;
        await api.post('/thoughts', { content: thought.trim(), priority: thoughtPriority });
      } else if (type === 'courses') {
        if (!shopName.trim()) return;
        await api.post('/shopping', { name: shopName.trim(), quantity: shopQty || null, category: shopCat });
      }
      onClose();
      window.dispatchEvent(new Event('family_refresh'));
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSave();
    if (e.key === 'Escape') onClose();
  };

  const TASK_ICONS = ['✅', '🗑️', '🧹', '👕', '🍳', '🐾', '📄', '💊', '🚗', '🛒', '📚', '🔧', '💡', '🌿', '🎁'];

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" onKeyDown={handleKeyDown}>
        <div className="modal-handle" />
        <div className="modal-title">Ajouter rapidement</div>

        <div className="tabs" style={{ marginBottom: 20 }}>
          {TYPES.map(t => (
            <button key={t.key} className={`tab${type === t.key ? ' active' : ''}`} onClick={() => setType(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {type === 'evenement' && (
          <>
            <div className="form-group">
              <input ref={inputRef} className="form-input" placeholder="Titre de l'événement..." value={evTitle} onChange={e => setEvTitle(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Date</label>
                <input type="date" className="form-input" value={evDate} onChange={e => setEvDate(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Heure</label>
                <input type="time" className="form-input" value={evTime} onChange={e => setEvTime(e.target.value)} placeholder="Toute la journée" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Pour qui ?</label>
                <select className="form-input" value={evWho} onChange={e => setEvWho(e.target.value)}>
                  <option value="">Toute la famille</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.avatar_emoji} {m.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Catégorie</label>
                <select className="form-input" value={evCat} onChange={e => setEvCat(e.target.value)}>
                  <option value="autre">Autre</option>
                  <option value="ecole">École</option>
                  <option value="sport">Sport</option>
                  <option value="medical">Médical</option>
                  <option value="famille">Famille</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
          </>
        )}

        {type === 'tache' && (
          <>
            <div className="form-group">
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                {TASK_ICONS.map(ic => (
                  <button key={ic} onClick={() => setTaskIcon(ic)}
                    style={{ width: 36, height: 36, border: taskIcon === ic ? '2px solid #6C63FF' : '2px solid #E0E4F0', borderRadius: 8, background: taskIcon === ic ? '#EEF0FF' : '#F0F2FF', fontSize: 18, cursor: 'pointer' }}>
                    {ic}
                  </button>
                ))}
              </div>
              <input ref={inputRef} className="form-input" placeholder="Titre de la tâche..." value={taskTitle} onChange={e => setTaskTitle(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Pour le</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: taskDueMode === 'date' ? 8 : 0 }}>
                {[
                  { key: 'today', label: "Aujourd'hui" },
                  { key: 'week', label: 'Cette semaine' },
                  { key: 'month', label: 'Ce mois-ci' },
                  { key: 'next_month', label: 'Mois prochain' },
                  { key: 'date', label: '📅 Date…' },
                  { key: 'none', label: 'Sans date' },
                ].map(opt => (
                  <button key={opt.key} onClick={() => setTaskDueMode(opt.key)}
                    style={{ padding: '5px 10px', borderRadius: 20, border: `2px solid ${taskDueMode === opt.key ? '#6C63FF' : '#E0E4F0'}`, background: taskDueMode === opt.key ? '#EEF0FF' : 'transparent', color: taskDueMode === opt.key ? '#6C63FF' : '#8888AA', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {opt.label}
                  </button>
                ))}
              </div>
              {taskDueMode === 'date' && (
                <input type="date" className="form-input" value={taskDue} onChange={e => setTaskDue(e.target.value)} />
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Assigné à</label>
              <select className="form-input" value={taskWho} onChange={e => setTaskWho(e.target.value)}>
                <option value="">Tout le monde</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.avatar_emoji} {m.name}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Priorité</label>
                <select className="form-input" value={taskPriority} onChange={e => setTaskPriority(e.target.value)}>
                  <option value="low">Faible</option>
                  <option value="medium">Normal</option>
                  <option value="high">Urgent</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Récurrence</label>
                <select className="form-input" value={taskRecurrence} onChange={e => setTaskRecurrence(e.target.value)}>
                  <option value="une_fois">Une fois</option>
                  <option value="quotidienne">Chaque jour</option>
                  <option value="hebdomadaire">Chaque semaine</option>
                  <option value="bimensuelle">Toutes les 2 sem.</option>
                  <option value="mensuelle">Chaque mois</option>
                </select>
              </div>
            </div>
          </>
        )}

        {type === 'pensee' && (
          <>
            <div className="form-group">
              <textarea ref={inputRef} className="form-input" placeholder="Qu'est-ce qui vous pèse ? Qu'est-ce que vous n'oubliez pas oublier ?&#10;Ex: plus de lait, vaccin de Sophie à prévoir, cadeau d'anniversaire de Paul..." value={thought} onChange={e => setThought(e.target.value)} rows={4} />
            </div>
            <div className="form-group">
              <label className="form-label">Niveau d'urgence</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['normal', '💬 Normal', '#2196F3'], ['high', '⚠️ Important', '#FF9800'], ['urgent', '🚨 Urgent', '#F44336']].map(([val, label, color]) => (
                  <button key={val} onClick={() => setThoughtPriority(val)}
                    style={{ flex: 1, padding: '8px 4px', border: `2px solid ${thoughtPriority === val ? color : '#E0E4F0'}`, borderRadius: 10, background: thoughtPriority === val ? color + '18' : 'transparent', color: thoughtPriority === val ? color : '#8888AA', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {type === 'courses' && (
          <>
            <div className="form-group">
              <input ref={inputRef} className="form-input" placeholder="Article à acheter..." value={shopName} onChange={e => setShopName(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Quantité</label>
                <input className="form-input" placeholder="Ex: 2, 500g, 1 litre..." value={shopQty} onChange={e => setShopQty(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Rayon</label>
                <select className="form-input" value={shopCat} onChange={e => setShopCat(e.target.value)}>
                  <option>Autre</option>
                  <option>Fruits & Légumes</option>
                  <option>Produits laitiers</option>
                  <option>Viandes & Poissons</option>
                  <option>Épicerie</option>
                  <option>Boissons</option>
                  <option>Hygiène</option>
                  <option>Entretien</option>
                  <option>Boulangerie</option>
                  <option>Surgelés</option>
                </select>
              </div>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" style={{ flex: 2 }} onClick={handleSave} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 18, height: 18 }} /> : '✓ Ajouter'}
          </button>
        </div>
        <div className="text-muted text-center mt-8" style={{ fontSize: 11 }}>Ctrl+Entrée pour sauvegarder · Échap pour fermer</div>
      </div>
    </div>
  );
}
